<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="pi-question">
        <span class="pi-bar pi-bar--xl"></span>
      </div>
      <div class="pi-image">
        <!-- Stylised "world map" with continents -->
        <svg viewBox="0 0 200 100" class="pi-map" preserveAspectRatio="none">
          <!-- background -->
          <rect width="200" height="100" fill="#D6E8FF" />
          <!-- continents -->
          <path d="M20 30 Q35 22 55 28 L62 42 Q50 50 38 48 L25 44 Z" fill="#B5E6BC" />
          <path d="M70 32 Q90 26 110 30 Q120 38 116 50 L92 56 L78 48 Z" fill="#B5E6BC" />
          <path d="M120 34 L150 30 L170 40 L168 58 L140 60 L122 50 Z" fill="#B5E6BC" />
          <path d="M50 62 L72 60 L80 80 L60 86 Z" fill="#B5E6BC" />
          <path d="M130 70 L155 68 L160 86 L140 88 Z" fill="#B5E6BC" />
        </svg>
        <span
          v-for="(p, i) in pins"
          :key="i"
          class="pi-pin"
          :class="{ '--landing': i === 0 }"
          :style="{ top: p.y + '%', left: p.x + '%', '--pin-i': i }"
        ></span>
      </div>
    </template>

    <template #audience>
      <span class="pi-phone-q"></span>
      <div class="pi-phone-image">
        <svg viewBox="0 0 100 80" class="pi-phone-map" preserveAspectRatio="none">
          <rect width="100" height="80" fill="#D6E8FF" />
          <path d="M15 20 Q30 14 45 20 L48 36 Q35 40 22 36 Z" fill="#B5E6BC" />
          <path d="M55 22 Q75 18 88 26 L82 44 L60 40 Z" fill="#B5E6BC" />
          <path d="M30 50 L52 48 L58 68 L36 70 Z" fill="#B5E6BC" />
        </svg>
        <span class="pi-phone-pin"></span>
        <span class="pi-phone-tap"></span>
      </div>
      <div class="pi-phone-hint">
        <span class="material-symbols-rounded">touch_app</span>
        Tap to drop a pin
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewPinOnImage',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      pins: [
        { x: 42, y: 38 }, // landing
        { x: 38, y: 42 },
        { x: 45, y: 35 },
        { x: 50, y: 40 },
        { x: 78, y: 48 },
        { x: 82, y: 52 },
        { x: 25, y: 36 },
        { x: 65, y: 70 },
        { x: 70, y: 75 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.pi-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.pi-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.pi-image {
  position: relative;
  flex: 1;
  min-height: 130px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(15, 15, 20, 0.06);
}
.pi-map { width: 100%; height: 100%; display: block; }

.pi-pin {
  position: absolute;
  width: 10px; height: 10px;
  border-radius: 999px;
  background: var(--accent);
  border: 2px solid white;
  transform: translate(-50%, -50%);
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.25);
}
.pi-pin.--landing {
  animation: pi-drop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}

/* ---- Audience phone ---- */
.pi-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.pi-phone-image {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent);
  min-height: 90px;
}
.pi-phone-map { width: 100%; height: 90px; display: block; }
.pi-phone-pin {
  position: absolute;
  top: 48%; left: 38%;
  width: 12px; height: 12px;
  border-radius: 999px;
  background: var(--accent);
  border: 2px solid white;
  transform: translate(-50%, -50%);
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.25);
  animation: pi-pop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.pi-phone-tap {
  position: absolute;
  top: 48%; left: 38%;
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid var(--accent);
  transform: translate(-50%, -50%);
  animation: pi-ripple 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.pi-phone-hint {
  display: flex; align-items: center; justify-content: center;
  gap: 4px;
  font-size: 10px; font-weight: 700;
  color: var(--accent);
}
.pi-phone-hint .material-symbols-rounded { font-size: 14px; }

@keyframes pi-drop {
  0%, 12% { transform: translate(-50%, -200%) scale(0.5); opacity: 0; }
  35%     { transform: translate(-50%, -50%) scale(1.3); opacity: 1; }
  50%, 92% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  100%    { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}
@keyframes pi-pop {
  0%, 8%  { transform: translate(-50%, -50%) scale(0); }
  25%     { transform: translate(-50%, -50%) scale(1.3); }
  40%, 100% { transform: translate(-50%, -50%) scale(1); }
}
@keyframes pi-ripple {
  0%, 8%  { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  25%     { opacity: 0.7; transform: translate(-50%, -50%) scale(1); }
  50%     { opacity: 0; transform: translate(-50%, -50%) scale(2.4); }
  100%    { opacity: 0; transform: translate(-50%, -50%) scale(2.4); }
}

@media (prefers-reduced-motion: reduce) {
  .pi-pin.--landing { animation: none; }
  .pi-phone-pin, .pi-phone-tap { animation: none; }
}
</style>
